import React, { useEffect, useState } from 'react'
import { makeStyles, Radio, RadioGroup, FormControlLabel, FormControl } from '@material-ui/core'
import { colors } from 'constants/index'
import { Button, ColorCircularProgress, ConfirmationModal, Icon, SvgIcon, ThreeDotsMenu, Typography } from 'components'
import { useDispatch, useSelector } from 'react-redux'
import { navigateToStripeConnect, redirectTo } from 'store/actions/navigation'
import { ReactComponent as StripeLogo } from 'static/stripe_logo.svg'
import {
  disconnectPaymentMethod,
  getPaymentConnectionStatus,
  redirectToStripeDashboard,
  savePaymentMethod,
} from 'store/actions/wallet/wallet'
import { getCurrentProfile, getCurrentProfileCharges, updatePayerForCharges } from 'store/actions/auth'
import { useQuery } from 'utils/hooks'

const useStyles = makeStyles({
  title: {
    fontSize: 20,
    fontWeight: 500,
    marginBottom: 4,
  },
  icon: {
    marginRight: 8,
    fontSize: 18,
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '15px 0',
    minWidth: 200,
    maxWidth: 600,
  },
  rowRightSide: {
    display: 'flex',
    alignItems: 'center',
  },
  stripeLogo: {
    height: 24,
    width: 58,
    marginRight: 24,
  },
  rowText: {
    color: colors.BASIC.COLOR[20],
    fontSize: 13,
  },
  paymentDesc: {
    maxWidth: 715,
    marginBottom: 24,
    fontSize: 15,
    fontWeight: 400,
    color: colors.BASIC.COLOR[100],
  },
  paragraph: {
    marginBottom: 12,
  },
  chargePaymentSection: {
    marginTop: 32,
    paddingTop: 24,
  },
  chargePaymentTitle: {
    fontSize: 16,
    fontWeight: 500,
    marginBottom: 8,
    color: colors.BASIC.COLOR[100],
  },
  chargePaymentDescription: {
    fontSize: 14,
    color: colors.BASIC.COLOR[70],
    marginBottom: 16,
    lineHeight: '1.4',
  },
  radioGroup: {
    display: 'flex',
    flexDirection: 'row',
    gap: 24,
  },
  radioLabel: {
    fontSize: 14,
    color: colors.BASIC.COLOR[100],
  },
})

const ProPaymentContainer = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const paymentAccountStatus = useSelector(state => state.wallet.paymentAccountStatus)
  const paymentAccountStatusIsLoading = useSelector(state => state.wallet.paymentAccountStatusIsLoading)
  const currentUser = useSelector(state => state.auth.currentUser)
  const [isConfirmationModalShown, setIsConfirmationModalShown] = useState(false)
  const [isSavingPaymentMethod, setIsSavingPaymentMethod] = useState(false)
  const [payerForCharges, setPayerForCharges] = useState('')
  const paymentMethodId = ''

  const onConfirmationModalConfirm = () => {
    savePaymentMethodCode()
  }

  const handlePayerForChargesChange = async event => {
    const newValue = event.target.value
    setPayerForCharges(newValue)
    try {
      await dispatch(updatePayerForCharges(newValue))
    } catch (error) {
      console.error('Error updating payer for charges:', error)
      // Revert the state on error
      setPayerForCharges(payerForCharges)
    }
  }

  useEffect(() => {
    dispatch(getPaymentConnectionStatus())
    getUserProfile()
  }, [])

  useEffect(() => {
    if (currentUser?.payer_for_charges) {
      setPayerForCharges(currentUser.payer_for_charges)
    }
  }, [currentUser?.payer_for_charges])

  const getUserProfile = async () => {
    try {
      const charges = await dispatch(getCurrentProfileCharges())
      setPayerForCharges(charges)
    } catch (e) {
      console.error(e)
    }
  }

  const savePaymentMethodCode = async code => {
    setIsSavingPaymentMethod(true)
    try {
      const response = await dispatch(savePaymentMethod(code))
      if (response?.link) {
        setIsConfirmationModalShown(false)
        setIsSavingPaymentMethod(false)
        window.location.href = response.link
      } else {
        setIsConfirmationModalShown(false)
        setIsSavingPaymentMethod(false)
        dispatch(redirectTo('/pro/account/settings/payment'))
        dispatch(getPaymentConnectionStatus())
      }
    } catch (e) {
      console.error(e)
      setIsSavingPaymentMethod(false)
    }
  }

  const actions = [
    {
      key: 'edit',
      label: 'edit',
      action: async () => {
        try {
          window.location.href = await dispatch(redirectToStripeDashboard())
        } catch (e) {
          console.error(e)
        }
      },
    },
    {
      key: 'disconnect',
      label: 'disconnect',
      action: async () => {
        try {
          await dispatch(disconnectPaymentMethod())
          dispatch(getPaymentConnectionStatus())
        } catch (e) {
          console.error(e)
        }
      },
    },
  ]

  return (
    <div className={classes.walletContainer}>
      <Typography variant="h4" className={classes.title}>
        Payment
      </Typography>
      {paymentAccountStatusIsLoading ? (
        <ColorCircularProgress />
      ) : paymentAccountStatus ? (
        <div className={classes.row}>
          <div className={classes.rowRightSide}>
            <SvgIcon viewBox="0 0 58 24" className={classes.stripeLogo}>
              <StripeLogo />
            </SvgIcon>
            <Typography className={classes.rowText}>account connected</Typography>
          </div>
          <div className={classes.rowActions}>
            <ThreeDotsMenu actions={actions} data={paymentMethodId} />
          </div>
        </div>
      ) : (
        <>
          {currentUser?.subrole === 'accountant' ? (
            <Typography className={classes.paymentDesc}>
              Electronic payments made by your clients will be held in your XIRA Wallet. In order to transfer funds from
              your wallet to your personal or business account, you need to connect your bank account. You may do this
              now or later when you wish to transfer funds.
            </Typography>
          ) : (
            <Typography className={classes.paymentDesc}>
              When clients pay you the funds go into your XIRA wallet. In order to have it transferred to your business
              account you need to connect your business account. You may do this now or later when you wish to transfer
              funds that have become available.
            </Typography>
          )}
          <Button variant="primary" onClick={() => setIsConfirmationModalShown(true)}>
            <Icon iconClass="las la-link" style={classes.icon} />
            {currentUser?.subrole === 'accountant' ? 'Connect bank account' : 'Connect payment method'}
          </Button>
        </>
      )}
      {currentUser?.subrole === 'accountant' && (
        <div className={classes.chargePaymentSection}>
          <Typography variant="h4" className={classes.title}>
            Charge payment
          </Typography>
          <Typography className={classes.paymentDesc}>
            When processing payments through XIRA, electronic charges (such as XIRA and Stripe fees) are applied to each
            transaction. You can choose whether these charges are paid by the client or by you.
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup className={classes.radioGroup} value={payerForCharges} onChange={handlePayerForChargesChange}>
              <FormControlLabel
                value="pro"
                control={<Radio color="primary" />}
                label="Paid by me"
                disabled={!payerForCharges}
                className={classes.radioLabel}
              />
              <FormControlLabel
                value="client"
                control={<Radio color="primary" />}
                label="Paid by clients"
                disabled={!payerForCharges}
                className={classes.radioLabel}
              />
            </RadioGroup>
          </FormControl>
        </div>
      )}
      <ConfirmationModal
        title="No banking information on file"
        message={
          <>
            <p className={classes.paragraph}>
              By clicking the button below you will be redirected to Stripe for creation of your Express account that
              will be used for payout. You will be asked for additional contact details and a debit card or your bank
              account depending on your choice.
            </p>

            <p className={classes.paragraph}>
              In case you already have a Stripe account on your current email, you will be asked to login to make
              registration faster.
            </p>

            <p className={classes.paragraph}>
              Once you've created the payment account you will be able to proceed with requesting funds.
            </p>
          </>
        }
        confirmationButtonText="ADD MY ACCOUNT"
        cancelationButtonText="Cancel"
        isOpen={isConfirmationModalShown}
        onClose={() => {
          if (!isSavingPaymentMethod) {
            setIsConfirmationModalShown(false)
            setIsSavingPaymentMethod(false)
          }
        }}
        onConfirm={() => onConfirmationModalConfirm()}
        disableButton={isSavingPaymentMethod}
      />
    </div>
  )
}

export default ProPaymentContainer
